import type { Scan } from "@hobbyshopify/backend-client";
import { cardSubtitle, formatCents, scanImage, scanTitle } from "../core/primitives/card-display";
import { formatDate } from "../core/primitives/format";

function Thumb({ src, alt }: { src: string | null; alt: string }) {
  return (
    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-xs text-faint">No img</div>
      )}
    </div>
  );
}

/**
 * A scan that has been confirmed into a card.
 *
 * The price shown is the card's, not the scan's: one price serves every copy
 * and every offer, so a scan no longer carries one of its own.
 */
/** A customer's confirmed scan, as their cart sees it. */
export interface ScanCartControls {
  readonly inCart: boolean;
  readonly canAdd: boolean;
  readonly busy: boolean;
  onAdd(): void;
  onRemove(): void;
}

export function ConfirmedScanRow({
  scan,
  onStock,
  onEdit,
  cart,
}: {
  scan: Scan;
  onStock?: () => void;
  onEdit: () => void;
  cart?: ScanCartControls;
}) {
  const card = scan.card;
  const title = scanTitle(scan);
  const subtitle = cardSubtitle(scan);

  return (
    <div className="flex gap-3 rounded-row border border-line bg-surface p-4">
      <Thumb src={scanImage(scan)} alt={title} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-medium text-white">{title}</p>
          <span className="shrink-0 text-xs text-faint">{formatDate(scan.createdAt)}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          {/* Accent-coloured, as prices are on iOS and in the customer app. */}
          <span className="text-sm font-semibold text-accent">{formatCents(card?.currentPriceCents)}</span>
          {subtitle && <span className="truncate text-xs text-faint">{subtitle}</span>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* The shop answered a price for a card it has none of — which happens
              when a catalogued card sells out and a loose copy turns up on the
              floor. Amber, because it is the merchant's to check before the
              card is handed over, not an error. */}
          {scan.heldCopy === false && (
            <span className="badge badge-warn whitespace-nowrap" title="This quote holds no copy">
              No copy held
            </span>
          )}
          {/* An untracked card is not "0 in stock" — the shop chose not to count
              it, so neither the count nor the stocking prompt belongs here. */}
          {card && !card.trackStock ? (
            <span className="badge badge-neutral whitespace-nowrap">Not tracked</span>
          ) : card && card.available > 0 ? (
            <span className="badge badge-neutral whitespace-nowrap">{card.available} in stock</span>
          ) : (
            onStock && (
              <button type="button" onClick={onStock} className="btn-primary btn-xs whitespace-nowrap">
                Add to Inventory
              </button>
            )
          )}
          <button type="button" onClick={onEdit} className="btn-chip">
            Edit
          </button>
          {cart?.inCart ? (
            <button type="button" onClick={cart.onRemove} disabled={cart.busy} className="btn-secondary btn-xs">
              {cart.busy ? "Removing…" : "Remove from cart"}
            </button>
          ) : cart?.canAdd ? (
            <button type="button" onClick={cart.onAdd} disabled={cart.busy} className="btn-primary btn-xs">
              {cart.busy ? "Adding…" : "Add to cart"}
            </button>
          ) : cart && scan.quoteState === "expired" ? (
            <span className="badge badge-warn whitespace-nowrap">Quote expired</span>
          ) : cart && scan.quoteState === "sold" ? (
            <span className="badge badge-neutral whitespace-nowrap">Sold</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * A scan nobody has reviewed yet.
 *
 * The badge says which of the two buckets it fell into, because that is the
 * only thing the reviewer needs before opening it: a match is a card the shop
 * already deals in and is usually one click, while a scan that matched nothing
 * is a new card and wants a proper look. Which key found it is not the
 * reviewer's business.
 */
export function PendingScanRow({ scan, onReview }: { scan: Scan; onReview: () => void }) {
  const matched = scan.matchKind === "PRODUCT" || scan.matchKind === "IDENTITY";
  const title = scanTitle(scan);
  const subtitle = cardSubtitle(scan);

  return (
    <div className="flex gap-3 rounded-row border border-line bg-surface p-4">
      <Thumb src={scanImage(scan)} alt={title} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-medium text-white">{title}</p>
          {/* Unreviewed work is amber, never the accent, which is reserved for
              what has been confirmed. */}
          <span className={`badge shrink-0 ${matched ? "badge-neutral" : "badge-warn"}`}>
            {matched ? "Already exists" : "New card"}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="text-sm font-semibold text-accent">{formatCents(scan.card?.currentPriceCents)}</span>
          {subtitle && <span className="truncate text-xs text-faint">{subtitle}</span>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-faint">{formatDate(scan.createdAt)}</span>
          {scan.heldCopy === false && (
            <span className="badge badge-warn whitespace-nowrap" title="This quote holds no copy">
              No copy held
            </span>
          )}
          <button type="button" onClick={onReview} className="btn-chip">
            Review
          </button>
        </div>
      </div>
    </div>
  );
}
